import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { revalidateStorefront } from '@/lib/actions/revalidate.actions';
import { TokenGetter, withAuthRetry } from '@/lib/apiClient';
import type { ProductVariant } from './productSlice';

const API_URL = process.env.NEXT_PUBLIC_API_BASE_URL
  ? `${process.env.NEXT_PUBLIC_API_BASE_URL}/products`
  : 'http://localhost:8080/products';
const ORDERS_API_URL = process.env.NEXT_PUBLIC_API_BASE_URL
  ? `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/v1/orders`
  : 'http://localhost:8080/api/v1/orders';
const STOCK_API_URL = process.env.NEXT_PUBLIC_API_BASE_URL
  ? `${process.env.NEXT_PUBLIC_API_BASE_URL}/products/stock/bulk`
  : 'http://localhost:8080/products/stock/bulk';
const INQUIRIES_API_URL = process.env.NEXT_PUBLIC_API_BASE_URL
  ? `${process.env.NEXT_PUBLIC_API_BASE_URL}/api/v1/corporate-gifts/inquiries`
  : 'http://localhost:8080/api/v1/corporate-gifts/inquiries';

// El backend devuelve { error, message } en los errores (GlobalExceptionHandler).
// Re-throw con el mensaje legible para que el toast del admin no muestre un
// genérico "Request failed with status code 409".
function throwReadableError(e: unknown): never {
  if (axios.isAxiosError(e) && e.response?.data) {
    const body = e.response.data as { message?: string; error?: string };
    const msg = body?.message || body?.error;
    if (typeof msg === 'string' && msg.trim()) {
      throw new Error(msg);
    }
  }
  throw e;
}

// Types
export interface OrderItem {
  id: number;
  quantity: number;
  price: number;
  hasCustomization?: boolean;
  // --- Variante (H1) — snapshot del backend C5 ---
  // `variantAttributes` viene como Map<String,String> en el JSON (Jackson
  // serializa Map nativamente, no necesita parse).
  variantId?: number;
  variantSku?: string;
  variantAttributes?: Record<string, string>;
  variantImageUrl?: string;
  // Snapshot del producto (ProductSummaryDTO del backend) para mostrar en UI
  product?: {
    id?: number;
    name: string;
    imageUrl?: string;
    customizationCost?: number;
  };
}

export interface Order {
  id: number;
  status: string;
  paymentStatus?: string;
  total: number;
  createdAt: string;
  items: OrderItem[];
  user?: {
    name: string;
    email: string;
    phoneNumber?: string;
  };
  guestFirstname?: string;
  guestLastname?: string;
  guestEmail?: string;
  guestPhone?: string;
  receiptUrl?: string;
}

export interface ProductData {
  name: string;
  description: string;
  price: number;
  /**
   * @deprecated Removido del form en E3. El stock vive en `variants[].stock`.
   * El backend ignora este campo desde A5. Queda opcional en el type para
   * no romper consumidores viejos; nuevo código no lo setea.
   */
  stock?: number;
  // El backend (`ProductPayload`) espera `categoryId: Long` flat. Antes E3-fix
  // el form mandaba `category: {id: N}` (shape viejo, no matcheaba) y el
  // backend tiraba 400 en cada submit. Ahora se manda `categoryId` flat.
  categoryId: number;
  images: {
    url: string;
    scale?: number;
    x?: number;
    y?: number;
    // E6: dirección imagen→variant. Null = imagen genérica.
    variantId?: number | null;
  }[];
  active: boolean;
  isCustomizable?: boolean;
  customizationCost?: number;
  // --- Variantes (D2 + E3 + E7) ---
  // E7: las variants son independientes con `name` libre. El backend crea
  // un default variant si la lista viene vacía.
  variants?: ProductVariant[];
}

interface AdminState {
  orders: Order[];
  ordersTotalElements: number;
  ordersTotalPages: number;
  ordersPage: number; // 0-indexed, espejo del `number` de Spring Page
  ordersPageSize: number;
  inquiries: CorporateGiftInquiry[];
  loading: boolean;
  error: string | null;
  successMessage: string | null;
}

export interface StockUpdateItem {
  sku: string;
  quantity: number;
}

// Consulta B2B de /regalos-empresariales (entidad CorporateGiftInquiry del backend)
export type InquiryStatus = 'NUEVO' | 'CONTACTADO' | 'CERRADO' | 'DESCARTADO';

export interface CorporateGiftInquiry {
  id: number;
  nombre: string;
  empresa?: string;
  email: string;
  telefono: string;
  cantidad?: string;
  tipoRegalo?: string;
  tipoMate?: string;
  presupuesto?: string;
  fechaEntrega?: string;
  comentarios?: string;
  status: InquiryStatus;
  createdAt: string;
}

export interface StockUpdateResponse {
  updated: number;
  notFoundSkus: string[];
}

export const fetchAllOrders = createAsyncThunk(
  'admin/fetchAllOrders',
  async (getToken: TokenGetter) => {
    const { data } = await withAuthRetry(getToken, (token) =>
      axios.get(ORDERS_API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
    return data;
  }
);

// Respuesta paginada de Spring Data (Page<OrderResponseDTO> serializado).
export interface OrdersPageResponse {
  content: Order[];
  totalElements: number;
  totalPages: number;
  number: number; // página actual, 0-indexed
  size: number;
}

// Paginado server-side: GET /api/v1/orders?page=0&size=10&status=PENDING.
// `status` en 'ALL'/undefined = sin filtro (el backend no filtra).
// Acepta tanto Page (nuevo) como List legacy para no romper si el backend
// viejo responde array.
export const fetchOrdersPage = createAsyncThunk(
  'admin/fetchOrdersPage',
  async ({
    getToken,
    page,
    size,
    status,
  }: {
    getToken: TokenGetter;
    page: number;
    size: number;
    status?: string;
  }) => {
    const params = new URLSearchParams({
      page: String(Math.max(0, page)),
      size: String(Math.max(1, Math.min(size, 50))),
    });
    if (status && status !== 'ALL') params.append('status', status);
    const { data } = await withAuthRetry(getToken, (token) =>
      axios.get<OrdersPageResponse | Order[]>(`${ORDERS_API_URL}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
    if (Array.isArray(data)) {
      return {
        content: data,
        totalElements: data.length,
        totalPages: 1,
        number: 0,
        size: data.length,
      } as OrdersPageResponse;
    }
    return data;
  }
);

// Thunk para actualizar un pedido (admin)
export const updateOrder = createAsyncThunk(
  'admin/updateOrder',
  async ({
    orderId,
    status,
    paymentStatus,
    total,
    getToken,
  }: {
    orderId: number;
    status: string;
    paymentStatus?: string;
    total: number;
    getToken: TokenGetter;
  }) => {
    const { data } = await withAuthRetry(getToken, (token) =>
      axios.put(
        `${ORDERS_API_URL}/${orderId}`,
        { status, paymentStatus, total },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      )
    );
    return data;
  }
);

// Thunk para eliminar un pedido (admin)
export const deleteOrder = createAsyncThunk(
  'admin/deleteOrder',
  async ({ orderId, getToken }: { orderId: number; getToken: TokenGetter }) => {
    await withAuthRetry(getToken, (token) =>
      axios.delete(`${ORDERS_API_URL}/${orderId}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
    );
    return orderId;
  }
);

export interface ManualOrderPayload {
  guestFirstname: string;
  guestLastname: string;
  guestEmail?: string;
  guestPhone: string;
  shippingPreference?: string;
  locality?: string;
  address?: string;
  floorApartment?: string;
  extraIndications?: string;
  status?: string;
  paymentStatus?: string;
  paymentMethod?: string;
  sendEmail?: boolean;
  total?: number;
  items: {
    variantId: number;
    quantity: number;
    hasCustomization?: boolean;
    unitPrice?: number;
  }[];
}

// Thunk para crear un pedido manualmente (admin)
export const createManualOrder = createAsyncThunk(
  'admin/createManualOrder',
  async ({ orderData, getToken }: { orderData: ManualOrderPayload; getToken: TokenGetter }) => {
    try {
      const { data } = await withAuthRetry(getToken, (token) =>
        axios.post<Order>(`${ORDERS_API_URL}/manual`, orderData, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        })
      );
      return data;
    } catch (e: unknown) {
      throwReadableError(e);
    }
  }
);

// Thunk para crear un producto
export const createProduct = createAsyncThunk(
  'admin/createProduct',
  async ({ productData, getToken }: { productData: ProductData; getToken: TokenGetter }) => {
    try {
      const { data } = await withAuthRetry(getToken, (token) =>
        axios.post(API_URL, productData, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        })
      );
      revalidateStorefront(['/shop']).catch((e) => console.error('Error revalidating /shop:', e));
      return data;
    } catch (e) {
      throwReadableError(e);
    }
  }
);

// Thunk para actualizar un producto
export const updateProduct = createAsyncThunk(
  'admin/updateProduct',
  async ({
    productId,
    productData,
    getToken,
  }: {
    productId: number;
    productData: ProductData;
    getToken: TokenGetter;
  }) => {
    try {
      const { data } = await withAuthRetry(getToken, (token) =>
        axios.put(`${API_URL}/${productId}`, productData, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        })
      );
      revalidateStorefront(['/shop']).catch((e) => console.error('Error revalidating /shop:', e));
      return data;
    } catch (e) {
      throwReadableError(e);
    }
  }
);

// Thunk para eliminar un producto
export const deleteProduct = createAsyncThunk(
  'admin/deleteProduct',
  async ({ productId, getToken }: { productId: number; getToken: TokenGetter }) => {
    await withAuthRetry(getToken, (token) =>
      axios.delete(`${API_URL}/${productId}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
    );
    revalidateStorefront(['/shop']).catch((e) => console.error('Error revalidating /shop:', e));
    return productId;
  }
);

// Thunk para actualización masiva de stock
export const bulkUpdateStock = createAsyncThunk(
  'admin/bulkUpdateStock',
  async ({ updates, getToken }: { updates: StockUpdateItem[]; getToken: TokenGetter }) => {
    const { data } = await withAuthRetry(getToken, (token) =>
      axios.patch<StockUpdateResponse>(STOCK_API_URL, updates, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
    );
    return data;
  }
);

// ---------- Consultas Mayoristas (B2B) ----------

export const fetchInquiries = createAsyncThunk(
  'admin/fetchInquiries',
  async (getToken: TokenGetter) => {
    const { data } = await withAuthRetry(getToken, (token) =>
      axios.get<CorporateGiftInquiry[]>(INQUIRIES_API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
    return data;
  }
);

export const updateInquiryStatus = createAsyncThunk(
  'admin/updateInquiryStatus',
  async ({
    inquiryId,
    status,
    getToken,
  }: {
    inquiryId: number;
    status: InquiryStatus;
    getToken: TokenGetter;
  }) => {
    const { data } = await withAuthRetry(getToken, (token) =>
      axios.patch<CorporateGiftInquiry>(
        `${INQUIRIES_API_URL}/${inquiryId}/status`,
        { status },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      )
    );
    return data;
  }
);

export const deleteInquiry = createAsyncThunk(
  'admin/deleteInquiry',
  async ({ inquiryId, getToken }: { inquiryId: number; getToken: TokenGetter }) => {
    await withAuthRetry(getToken, (token) =>
      axios.delete(`${INQUIRIES_API_URL}/${inquiryId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
    );
    return inquiryId;
  }
);

const adminSlice = createSlice({
  name: 'admin',
  initialState: {
    orders: [],
    ordersTotalElements: 0,
    ordersTotalPages: 1,
    ordersPage: 0,
    ordersPageSize: 10,
    inquiries: [],
    loading: false,
    error: null,
    successMessage: null,
  } as AdminState,
  reducers: {
    clearAdminMessages: (state) => {
      state.error = null;
      state.successMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Product
      .addCase(createProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createProduct.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Producto creado exitosamente';
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al crear producto';
      })
      // Update Product
      .addCase(updateProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateProduct.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Producto actualizado exitosamente';
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al actualizar producto';
      })
      // Delete Product
      .addCase(deleteProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteProduct.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Producto eliminado exitosamente';
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al eliminar producto';
      })
      // Fetch Orders (legacy: lista completa, sin paginar)
      .addCase(fetchAllOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;
        state.ordersTotalElements = action.payload?.length || 0;
        state.ordersTotalPages = Math.max(
          1,
          Math.ceil((action.payload?.length || 0) / state.ordersPageSize)
        );
      })
      .addCase(fetchAllOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar pedidos';
      })
      // Fetch Orders Page (server-side: GET /orders?page&size&status)
      .addCase(fetchOrdersPage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrdersPage.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.content;
        state.ordersTotalElements = action.payload.totalElements;
        state.ordersTotalPages = Math.max(1, action.payload.totalPages);
        state.ordersPage = action.payload.number;
        state.ordersPageSize = action.payload.size;
      })
      .addCase(fetchOrdersPage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar pedidos';
      })
      // Update Order
      .addCase(updateOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Pedido actualizado exitosamente';
        const index = state.orders.findIndex((o) => o.id === action.payload.id);
        if (index !== -1) {
          state.orders[index] = action.payload;
        }
      })
      .addCase(updateOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al actualizar pedido';
      })
      // Delete Order
      .addCase(deleteOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteOrder.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Pedido eliminado exitosamente';
        state.orders = state.orders.filter((order) => order.id !== action.payload);
        // Mantener el contador server-side en sync (el componente re-fetchea la página).
        state.ordersTotalElements = Math.max(0, state.ordersTotalElements - 1);
        state.ordersTotalPages = Math.max(
          1,
          Math.ceil(state.ordersTotalElements / Math.max(1, state.ordersPageSize))
        );
      })
      .addCase(deleteOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al eliminar pedido';
      })
      // Create Manual Order
      .addCase(createManualOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createManualOrder.fulfilled, (state) => {
        state.loading = false;
        state.successMessage = 'Pedido manual creado exitosamente';
        // En modo paginado no se hace unshift (rompería el tamaño de página):
        // el componente re-fetchea la página actual. Solo se ajusta el total.
        state.ordersTotalElements += 1;
        state.ordersTotalPages = Math.max(
          1,
          Math.ceil(state.ordersTotalElements / Math.max(1, state.ordersPageSize))
        );
      })
      .addCase(createManualOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al crear pedido manual';
      })
      // Bulk Update Stock
      .addCase(bulkUpdateStock.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(bulkUpdateStock.fulfilled, (state, action) => {
        state.loading = false;
        const { updated, notFoundSkus } = action.payload;
        if (notFoundSkus.length > 0) {
          state.error = `SKUs no encontrados: ${notFoundSkus.join(', ')}`;
        }
        state.successMessage = `Stock actualizado para ${updated} variante(s)`;
      })
      .addCase(bulkUpdateStock.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al actualizar stock';
      })
      // Fetch Inquiries
      .addCase(fetchInquiries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInquiries.fulfilled, (state, action) => {
        state.loading = false;
        state.inquiries = action.payload;
      })
      .addCase(fetchInquiries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al cargar consultas';
      })
      // Update Inquiry Status
      .addCase(updateInquiryStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(updateInquiryStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Estado actualizado exitosamente';
        const index = state.inquiries.findIndex((i) => i.id === action.payload.id);
        if (index !== -1) {
          state.inquiries[index] = action.payload;
        }
      })
      .addCase(updateInquiryStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al actualizar el estado';
      })
      // Delete Inquiry
      .addCase(deleteInquiry.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(deleteInquiry.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = 'Consulta eliminada exitosamente';
        state.inquiries = state.inquiries.filter((inquiry) => inquiry.id !== action.payload);
      })
      .addCase(deleteInquiry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Error al eliminar la consulta';
      });
  },
});

export const { clearAdminMessages } = adminSlice.actions;
export default adminSlice.reducer;
